import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Image as ImageIcon,
  Upload,
  X,
  Edit2,
  Wand2,
  Loader2,
  Play,
  Trash2,
  Film
} from 'lucide-react';
import toast from 'react-hot-toast';
import VideoFramePicker from './VideoFramePicker';
import GalleryPicker from '../video-studio/GalleryPicker';
import AIImageReferenceInput, { AIImageReferenceHandle } from './AIImageReferenceInput';

export interface MediaPickerProps {
  // ─── Image ───
  imageUrl: string | null;
  /** Called when the image URL changes (after upload / generation / removal). */
  onImageChange: (url: string | null) => void;
  /** Show the image section at all. Default: true. */
  imageSupported?: boolean;
  /** Mark the image as required with a red asterisk. Default: false. */
  imageRequired?: boolean;
  recommendedImageSize?: string;
  allowedImageFormats?: string[];
  /** Optional explanatory hint below the label (amber). */
  imageHint?: string | null;
  /** Optional edit button — if provided, shows an Edit icon over the image. */
  onEditImage?: () => void;

  // ─── Video ───
  videoUrl?: string;
  onVideoChange?: (url: string) => void;
  videoSupported?: boolean;
  videoRequired?: boolean;
  recommendedVideoSize?: string;
  /** Optional explanatory hint below the label (amber). */
  videoHint?: string | null;
  videoLimitMB?: number; // default 200

  // ─── AI generation ───
  aiEnabled?: boolean; // default true
  imagePrompt?: string;
  onImagePromptChange?: (v: string) => void;
  imageStyle?: string; // e.g. 'realistic', 'artistic'
  onImageStyleChange?: (v: string) => void;
  aspectRatio?: string; // e.g. '1:1'
  onAspectRatioChange?: (v: string) => void;
  /** Whether to show the aspect ratio selector. Default: false (use a size string instead). */
  showAspectRatio?: boolean;
  imageStyleOptions?: Array<{ value: string; label: string }>;
  aspectRatioOptions?: Array<{ value: string; label: string }>;

  // ─── Services ───
  /** Upload a File → return the hosted URL. */
  uploadImage: (file: File) => Promise<string>;
  /** Upload a video File → return the hosted URL. Required if videoSupported. */
  uploadVideo?: (file: File) => Promise<string>;
  /** Generate an AI image and return the hosted URL. */
  generateImage: (params: {
    prompt: string;
    style: string;
    aspectRatio?: string;
    referenceImageUrl?: string;
  }) => Promise<string>;
  /** Upload a reference image (for AI) → return the hosted URL. */
  uploadReference: (file: File) => Promise<{ url: string }>;

  /** Disables all interaction. */
  disabled?: boolean;
}

const DEFAULT_STYLES = [
  { value: 'realistic', label: 'Realista' },
  { value: 'artistic', label: 'Artístico' },
  { value: 'minimal', label: 'Minimalista' },
  { value: 'vibrant', label: 'Vibrante' },
  { value: 'professional', label: 'Profissional' }
];

const DEFAULT_ASPECTS = [
  { value: '1:1', label: '1:1 (Square)' },
  { value: '4:5', label: '4:5 (Portrait)' },
  { value: '16:9', label: '16:9 (Landscape)' },
  { value: '9:16', label: '9:16 (Vertical)' }
];

const MediaPicker: React.FC<MediaPickerProps> = ({
  imageUrl,
  onImageChange,
  imageSupported = true,
  imageRequired = false,
  recommendedImageSize,
  allowedImageFormats = ['jpg', 'png'],
  imageHint,
  onEditImage,
  videoUrl = '',
  onVideoChange,
  videoSupported = true,
  videoRequired = false,
  recommendedVideoSize,
  videoHint,
  videoLimitMB = 2048,
  aiEnabled = true,
  imagePrompt = '',
  onImagePromptChange,
  imageStyle = 'realistic',
  onImageStyleChange,
  aspectRatio = '1:1',
  onAspectRatioChange,
  showAspectRatio = false,
  imageStyleOptions = DEFAULT_STYLES,
  aspectRatioOptions = DEFAULT_ASPECTS,
  uploadImage,
  uploadVideo,
  generateImage,
  uploadReference,
  disabled = false
}) => {
  const { t } = useTranslation();
  // Human-friendly limit label (e.g. "2GB" instead of "2048MB").
  const videoLimitLabel = videoLimitMB >= 1024
    ? `${Number.isInteger(videoLimitMB / 1024) ? videoLimitMB / 1024 : (videoLimitMB / 1024).toFixed(1)}GB`
    : `${videoLimitMB}MB`;
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const videoInputRef = useRef<HTMLInputElement | null>(null);
  const aiRefHandle = useRef<AIImageReferenceHandle | null>(null);

  const [uploadingImage, setUploadingImage] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [showVideoLibrary, setShowVideoLibrary] = useState<'video' | 'image' | null>(null);
  /** Keeps the locally-selected video File so the frame picker can read it without CORS issues. */
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [showFramePicker, setShowFramePicker] = useState(false);

  // ─── Image upload ───
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setUploadingImage(true);
    try {
      const url = await uploadImage(file);
      onImageChange(url);
    } catch (err: any) {
      console.error('Image upload failed:', err);
      toast.error(err?.message || (t('shared.mediaPicker.uploadImageError', 'Erro ao enviar imagem') as string));
    } finally {
      setUploadingImage(false);
    }
  };

  // ─── AI generate ───
  const handleGenerate = async () => {
    const prompt = (imagePrompt || '').trim();
    if (!prompt) {
      toast.error(t('shared.mediaPicker.enterPrompt', 'Descreva a imagem que deseja gerar'));
      return;
    }
    setGeneratingImage(true);
    try {
      let refUrl: string | null = null;
      try {
        refUrl = (await aiRefHandle.current?.resolve()) || null;
      } catch (upErr: any) {
        toast.error(upErr?.response?.data?.message || upErr?.message || (t('imageStudio.reference.uploadError', 'Failed to upload reference image.') as string));
        setGeneratingImage(false);
        return;
      }

      const url = await generateImage({
        prompt,
        style: imageStyle,
        aspectRatio: showAspectRatio ? aspectRatio : undefined,
        referenceImageUrl: refUrl || undefined
      });
      if (url) {
        onImageChange(url);
        toast.success(t('shared.mediaPicker.imageGenerated', 'Imagem gerada com sucesso!'));
        aiRefHandle.current?.clear();
      } else {
        throw new Error('No URL returned');
      }
    } catch (err: any) {
      console.error('AI generate failed:', err);
      toast.error(err?.response?.data?.message || err?.message || (t('shared.mediaPicker.generateError', 'Erro ao gerar imagem') as string));
    } finally {
      setGeneratingImage(false);
    }
  };

  // ─── Video upload ───
  const handleVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > videoLimitMB) {
      toast.error(t('shared.mediaPicker.videoTooLarge', 'Vídeo muito grande. Limite: {{limit}}', { limit: videoLimitLabel }));
      return;
    }
    if (!uploadVideo) return;

    setVideoFile(file);
    setUploadingVideo(true);
    try {
      const url = await uploadVideo(file);
      onVideoChange?.(url);
    } catch (err: any) {
      console.error('Video upload failed:', err);
      setVideoFile(null);
      toast.error(err?.message || (t('shared.mediaPicker.uploadVideoError', 'Erro ao enviar vídeo') as string));
    } finally {
      setUploadingVideo(false);
    }
  };

  const clearVideo = () => {
    onVideoChange?.('');
    setVideoFile(null);
  };

  // ─── Frame picker handler ───
  const handleFrameSelected = async (file: File) => {
    setShowFramePicker(false);
    setUploadingImage(true);
    try {
      const url = await uploadImage(file);
      onImageChange(url);
      toast.success(t('shared.mediaPicker.frameSelected', 'Frame definido como imagem!'));
    } catch (err: any) {
      console.error('Frame upload failed:', err);
      toast.error(err?.message || (t('shared.mediaPicker.uploadImageError', 'Erro ao enviar imagem') as string));
    } finally {
      setUploadingImage(false);
    }
  };

  // ─── Video block (rendered first so users upload video before the image/thumb) ───
  const videoBlock = (videoSupported && uploadVideo) ? (
    <div>
      <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2 mb-2 flex-wrap">
        <Play className="w-4 h-4" />
        {t('shared.mediaPicker.video', 'Vídeo')}
        {videoRequired ? (
          <span className="text-red-500">*</span>
        ) : (
          <span className="text-xs text-gray-400 font-normal">({t('common.optional', 'opcional')})</span>
        )}
        {recommendedVideoSize && (
          <span className="text-xs text-gray-400 font-normal">({recommendedVideoSize})</span>
        )}
        {videoHint && (
          <span className="text-xs text-amber-600 dark:text-amber-400 font-normal">{videoHint}</span>
        )}
      </label>

      {videoUrl ? (
        <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <Play className="w-5 h-5 text-green-600 flex-shrink-0" />
          <span className="text-sm text-green-700 dark:text-green-300 truncate flex-1">
            {videoUrl.split('/').pop() || videoUrl}
          </span>
          <button
            type="button"
            onClick={clearVideo}
            className="p-1 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 rounded"
            disabled={disabled}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          <input
            ref={videoInputRef}
            type="file"
            accept="video/*"
            onChange={handleVideoUpload}
            className="hidden"
            disabled={disabled || uploadingVideo}
          />
          <button
            type="button"
            onClick={() => videoInputRef.current?.click()}
            disabled={disabled || uploadingVideo}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/10 transition-colors disabled:cursor-wait"
          >
            {uploadingVideo ? (
              <Loader2 className="w-5 h-5 text-primary-500 animate-spin" />
            ) : (
              <Upload className="w-5 h-5 text-gray-400" />
            )}
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {uploadingVideo ? t('shared.mediaPicker.uploadingVideo', 'Enviando...') : t('shared.mediaPicker.uploadVideo', 'Upload de vídeo')}
            </span>
          </button>

          {/* Use from Video Studio library */}
          <button
            type="button"
            onClick={() => setShowVideoLibrary('video')}
            disabled={disabled}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-cyan-500/10 border border-cyan-500/30 text-cyan-700 dark:text-cyan-300 text-sm font-semibold hover:bg-cyan-500/20 transition disabled:opacity-50"
          >
            <Play className="w-4 h-4" />
            {t('shared.mediaPicker.useFromVideoLibrary', 'Usar da biblioteca de vídeos')}
          </button>

          <div className="flex items-center gap-2">
            <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
            <span className="text-xs text-gray-400">{t('common.or', 'ou')}</span>
            <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
          </div>
          <input
            type="url"
            value={videoUrl || ''}
            onChange={(e) => onVideoChange?.(e.target.value)}
            placeholder="https://..."
            className="input text-sm w-full"
            disabled={disabled}
          />
          <p className="text-xs text-gray-400">
            {t('shared.mediaPicker.videoLimit', 'Formatos: MP4, MOV, AVI. Limite: {{limit}}', { limit: videoLimitLabel })}
          </p>
        </div>
      )}
    </div>
  ) : null;

  return (
    <div className="space-y-6">
      {/* ─── Video first (so the frame-picker flow feels natural after upload) ─── */}
      {videoBlock}

      {/* ─── Image ─── */}
      {imageSupported && (
        <div>
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2 mb-2 flex-wrap">
            <ImageIcon className="w-4 h-4" />
            {t('shared.mediaPicker.image', 'Imagem')}
            {imageRequired && <span className="text-red-500">*</span>}
            {recommendedImageSize && (
              <span className="text-xs text-gray-400 font-normal">({recommendedImageSize})</span>
            )}
            {imageHint && (
              <span className="text-xs text-amber-600 dark:text-amber-400 font-normal">{imageHint}</span>
            )}
          </label>

          {imageUrl ? (
            <div className="relative rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-800">
              <img src={imageUrl} alt="Preview" className="w-full h-48 object-cover" />
              <div className="absolute top-2 right-2 flex gap-1.5">
                {onEditImage && (
                  <button
                    type="button"
                    onClick={onEditImage}
                    title={t('shared.mediaPicker.edit', 'Editar imagem') as string}
                    className="p-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => onImageChange(null)}
                  className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                  disabled={disabled}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ) : (
            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-4 space-y-4">
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
                disabled={disabled || uploadingImage}
              />
              <button
                type="button"
                onClick={() => imageInputRef.current?.click()}
                disabled={disabled || uploadingImage}
                className="w-full flex flex-col items-center justify-center py-4 cursor-pointer disabled:cursor-wait"
              >
                {uploadingImage ? (
                  <Loader2 className="w-6 h-6 text-primary-500 animate-spin mb-2" />
                ) : (
                  <Upload className="w-8 h-8 text-gray-400 mb-2" />
                )}
                <span className="text-sm text-gray-500">
                  {uploadingImage
                    ? t('shared.mediaPicker.uploadingImage', 'Enviando imagem...')
                    : t('shared.mediaPicker.uploadImage', 'Upload de imagem')}
                </span>
                <span className="text-xs text-gray-400 mt-1">{allowedImageFormats.join(', ').toUpperCase()}</span>
              </button>

              {/* Pick from existing image library */}
              <button
                type="button"
                onClick={() => setShowVideoLibrary('image')}
                disabled={disabled}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-300 text-sm font-semibold hover:bg-amber-500/20 transition disabled:opacity-50"
              >
                <ImageIcon className="w-4 h-4" />
                {t('shared.mediaPicker.useFromImageLibrary', 'Usar da biblioteca de imagens')}
              </button>

              {/* Frame picker trigger — only when a video is present */}
              {videoUrl && (
                <button
                  type="button"
                  onClick={() => setShowFramePicker(true)}
                  className="w-full inline-flex items-center justify-center gap-1.5 py-2 rounded-lg border border-indigo-300 dark:border-indigo-700 text-indigo-600 dark:text-indigo-400 text-xs font-medium hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
                >
                  <Film className="h-3.5 w-3.5" />
                  {t('shared.mediaPicker.pickFromVideo', 'Escolher frame do vídeo')}
                </button>
              )}

              {aiEnabled && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-2">
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    <Wand2 className="w-4 h-4 text-purple-500" />
                    {t('shared.mediaPicker.generateWithAI', 'Gerar com IA')}
                  </div>
                  <textarea
                    value={imagePrompt}
                    onChange={(e) => onImagePromptChange?.(e.target.value)}
                    placeholder={t('shared.mediaPicker.imagePromptPlaceholder', 'Descreva a imagem que deseja gerar...') as string}
                    className="input min-h-[60px] text-sm w-full"
                    rows={2}
                    disabled={disabled || generatingImage}
                  />
                  <div className="flex gap-2 flex-wrap">
                    {imageStyleOptions.length > 0 && (
                      <select
                        value={imageStyle}
                        onChange={(e) => onImageStyleChange?.(e.target.value)}
                        className="input py-1.5 text-sm flex-1 min-w-[140px]"
                        disabled={disabled || generatingImage}
                      >
                        {imageStyleOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>{t(opt.label, opt.label)}</option>
                        ))}
                      </select>
                    )}
                    {showAspectRatio && (
                      <select
                        value={aspectRatio}
                        onChange={(e) => onAspectRatioChange?.(e.target.value)}
                        className="input py-1.5 text-sm flex-1 min-w-[140px]"
                        disabled={disabled || generatingImage}
                      >
                        {aspectRatioOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>{t(opt.label, opt.label)}</option>
                        ))}
                      </select>
                    )}
                    <button
                      type="button"
                      onClick={handleGenerate}
                      disabled={disabled || generatingImage || !imagePrompt.trim()}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 text-white text-sm font-medium hover:from-purple-600 hover:to-pink-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {generatingImage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                      {t('common.generate', 'Gerar')}
                    </button>
                  </div>

                  <AIImageReferenceInput
                    ref={aiRefHandle}
                    uploadReference={uploadReference}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Frame picker modal */}
      {showFramePicker && videoUrl && (
        <VideoFramePicker
          videoFile={videoFile}
          videoUrl={videoUrl}
          onClose={() => setShowFramePicker(false)}
          onSelect={handleFrameSelected}
        />
      )}

      {/* Gallery picker modal — pick existing video/image asset from the user's library */}
      <GalleryPicker
        open={showVideoLibrary !== null}
        source={showVideoLibrary}
        onClose={() => setShowVideoLibrary(null)}
        onPick={(asset) => {
          const url = asset.url || asset.thumbnail_url;
          if (!url) {
            toast.error(t('shared.mediaPicker.assetMissingUrl', 'Esse item não tem URL disponível') as string);
            return;
          }
          if (showVideoLibrary === 'video') {
            onVideoChange?.(url);
          } else {
            onImageChange(url);
          }
          setShowVideoLibrary(null);
        }}
      />

    </div>
  );
};

export default MediaPicker;
